import React from 'react';
import { Loader2 } from 'lucide-react';

/** Purpose: Renders request send preview + email compose modal. Inputs: modal props. Output: React render output (JSX). */
export default function SendPreviewModal({
  open,
  request,
  recordTypeLabels,
  canComposeEmail,
  emailTo,
  setEmailTo,
  emailSubject,
  setEmailSubject,
  emailMessage,
  setEmailMessage,
  sendError,
  gmailDraftError,
  onClose,
  onDownloadPackage,
  onConfirmSentManual,
  onCreateGmailDraft,
  onSendViaGmail,
  onSend,
  hasPortalLink,
  isUpdating
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl">
          <h3 className="text-lg font-semibold text-white">Request Package Preview</h3>
          <p className="text-blue-100 text-sm">
            Review the package details before sending.
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-slate-900">Package Contents</h4>
            <ul className="text-sm text-slate-700 space-y-1">
              <li>Cover letter with request details</li>
              <li>HIPAA authorization (signed)</li>
              <li>Requested record types: {recordTypeLabels || 'N/A'}</li>
              {request.specificRecords && (
                <li>Specific records: {request.specificRecords}</li>
              )}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-500">Provider</p>
              <p className="font-medium text-slate-900">{request.providerName}</p>
              <p className="text-slate-600">{request.organization}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-500">Delivery Method</p>
              <p className="font-medium text-slate-900">{request.deliveryMethod}</p>
              {request.email && <p className="text-slate-600">{request.email}</p>}
              {request.fax && <p className="text-slate-600">{request.fax}</p>}
            </div>
          </div>

          {(request.destinationProviderName || request.destinationOrganization || request.destinationEmail || request.destinationFax) && (
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <p className="text-slate-500">Destination Provider</p>
              <p className="font-medium text-slate-900">
                {[request.destinationProviderName, request.destinationOrganization].filter(Boolean).join(' - ') || 'N/A'}
              </p>
              {(request.destinationAddress || request.destinationCity || request.destinationState || request.destinationPostalCode) && (
                <p className="text-slate-600">
                  {[request.destinationAddress, request.destinationCity, request.destinationState, request.destinationPostalCode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              {request.destinationEmail && <p className="text-slate-600">{request.destinationEmail}</p>}
              {request.destinationFax && <p className="text-slate-600">{request.destinationFax}</p>}
              {request.destinationPhone && <p className="text-slate-600">{request.destinationPhone}</p>}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-medium text-slate-900">Compose Email</h4>
                <p className="text-xs text-slate-600 mt-1">
                  This email will include the request package PDF attachment.
                </p>
                {!hasPortalLink && (
                  <p className="text-xs text-amber-700 mt-2">
                    Generate the provider upload link first, then send email.
                  </p>
                )}
              </div>
              {!canComposeEmail && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Email missing
                </span>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">To</label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="provider@example.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Subject</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Message</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {(sendError || gmailDraftError) && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              {sendError || gmailDraftError}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-4 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
          >
            Close
          </button>
          <div className="flex gap-2">
            <button
              onClick={onDownloadPackage}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
            >
              Download Package
            </button>
            <button
              onClick={onCreateGmailDraft}
              disabled={isUpdating || !hasPortalLink}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
              title="Creates a draft in your connected Gmail inbox so you can send from the email the provider has on file."
            >
              Create Gmail Draft
            </button>
            <button
              onClick={onSendViaGmail}
              disabled={isUpdating || !hasPortalLink}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
              title="Sends immediately from your connected Gmail account without leaving MedCaddy."
            >
              {isUpdating && <Loader2 className="w-4 h-4 animate-spin" />}
              Send from Connected Gmail
            </button>
            <button
              onClick={onConfirmSentManual}
              disabled={isUpdating}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Confirm Sent Manually
            </button>
            <button
              onClick={onSend}
              disabled={isUpdating || !hasPortalLink}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isUpdating && <Loader2 className="w-4 h-4 animate-spin" />}
              Send Email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
